import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { env } from '../config/env';
import { buildSnippets } from './snippets';
import { copyText } from '../utils/clipboard';
import { Page, PageHeader, InferenceTabs, INFERENCE_EYEBROW } from '../components/console/ui';

const GATEWAY_BASE_URL = `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1`;

type CodeLang = 'curl' | 'python' | 'javascript';

const STREAMING_SNIPPET = `from openai import OpenAI

client = OpenAI(
    base_url="${GATEWAY_BASE_URL}",
    api_key="PODSTACK_API_KEY",
)

stream = client.chat.completions.create(
    model="model-name",
    messages=[{"role": "user", "content": "Hello"}],
    stream=True,
)
for chunk in stream:
    delta = chunk.choices[0].delta.content
    if delta:
        print(delta, end="")`;

const ERROR_ROWS: { status: string; code: string; meaning: string; action: string }[] = [
  {
    status: '402',
    code: 'insufficient_funds',
    meaning: 'Your wallet balance is too low to cover this request.',
    action: 'Top up your wallet, then retry.',
  },
  {
    status: '429',
    code: 'token_limit_reached',
    meaning: 'A configured monthly token limit or rate limit for this key has been hit.',
    action: 'Wait for the window to reset, raise the limit on the API keys page, or slow down request volume.',
  },
  {
    status: '503',
    code: 'billing_unavailable',
    meaning: 'The billing subsystem could not be reached to authorize this request.',
    action: 'Safe to retry - use exponential backoff.',
  },
  {
    status: '5xx',
    code: 'upstream_error',
    meaning: 'A transient failure occurred while generating the response.',
    action: 'Safe to retry - transient by nature.',
  },
];

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const copy = () => {
    void copyText(code);
  };
  return (
    <div className="relative">
      {label && <div className="mb-1.5 text-xs text-ps-subtle">{label}</div>}
      <div className="relative">
        <pre className="ps-pre max-h-96 overflow-auto whitespace-pre-wrap break-words pr-12">{code}</pre>
        <button className="ps-cb absolute right-2 top-2" onClick={copy} aria-label="Copy to clipboard">
          <ClipboardIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

const LANG_LABEL: Record<CodeLang, string> = { curl: 'curl', python: 'Python', javascript: 'JavaScript' };

const TOC = [
  { id: 'quickstart', label: 'Quickstart' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'streaming', label: 'Streaming' },
  { id: 'models-pricing', label: 'Models & pricing' },
  { id: 'errors', label: 'Errors' },
  { id: 'rate-limits', label: 'Rate limits' },
];

const ic = 'rounded bg-ps-raised px-1.5 py-0.5 text-ps-text';
const section = 'ps-card flex scroll-mt-24 flex-col gap-3 p-[22px]';
const body = 'm-0 text-[13px] leading-[1.65] text-ps-muted';
const h2 = 'm-0 text-xl font-medium';

const Docs = () => {
  const [lang, setLang] = useState<CodeLang>('curl');

  const snippets = useMemo(
    () =>
      buildSnippets({
        baseUrl: GATEWAY_BASE_URL,
        model: 'model-name',
        apiKeyLabel: 'PODSTACK_API_KEY',
      }),
    []
  );

  return (
    <Page>
      <PageHeader
        eyebrow={INFERENCE_EYEBROW}
        title="Docs"
        subtitle="Everything you need to call the Podstack Inference API. OpenAI SDK-compatible."
      />

      <InferenceTabs />

      <div className="flex flex-wrap items-start gap-6">
        <nav
          aria-label="On this page"
          className="ps-card flex flex-[1_1_200px] flex-col gap-0.5 p-2 min-[1101px]:sticky min-[1101px]:top-[88px]"
        >
          {TOC.map((t) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              className="flex min-h-[36px] items-center rounded-md px-2.5 text-[13px] text-ps-muted transition-colors hover:bg-ps-hover hover:text-ps-text"
            >
              {t.label}
            </a>
          ))}
        </nav>

        <div className="flex min-w-0 flex-[4_1_560px] flex-col gap-5">
          {/* Quickstart */}
          <section id="quickstart" className={section}>
            <h2 className={h2}>Quickstart</h2>
            <p className={body}>
              The Inference API is OpenAI SDK-compatible. Point your client at the Podstack gateway base URL below and
              use one of your API keys to authenticate.
            </p>
            <div className="ps-copy">
              <span className="text-ps-subtle">Base URL</span>
              <code className="text-ps-text">{GATEWAY_BASE_URL}</code>
              <button type="button" className="ps-cb" onClick={() => void copyText(GATEWAY_BASE_URL)}>
                Copy
              </button>
            </div>
            <div className="ps-seg self-start" role="group" aria-label="Language">
              {(['curl', 'python', 'javascript'] as CodeLang[]).map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={lang === l}
                  className={lang === l ? 'ps-seg-on' : ''}
                  onClick={() => setLang(l)}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>
            <CodeBlock code={snippets[lang]} />
          </section>

          {/* Authentication */}
          <section id="authentication" className={section}>
            <h2 className={h2}>Authentication</h2>
            <p className={body}>Every request must include your API key as a bearer token:</p>
            <div className="ps-copy">
              <code className="text-ps-text">Authorization: Bearer psk_…</code>
            </div>
            <p className={body}>
              Create and manage keys on the{' '}
              <Link to="/console/keys" className="ps-link">
                API keys
              </Link>{' '}
              page. Keys are shown only once at creation - store them securely.
            </p>
          </section>

          {/* Streaming */}
          <section id="streaming" className={section}>
            <h2 className={h2}>Streaming</h2>
            <p className={body}>
              Set <code className={ic}>"stream": true</code> on a chat completion request to receive a server-sent
              events (SSE) stream of incremental deltas instead of a single JSON response.
            </p>
            <CodeBlock code={STREAMING_SNIPPET} label="Python streaming" />
          </section>

          {/* Models & pricing */}
          <section id="models-pricing" className={section}>
            <h2 className={h2}>Models &amp; pricing</h2>
            <div>
              <div className="flex flex-wrap justify-between gap-3 py-2.5 text-[13px]">
                <code className="text-ps-text">GET /v1/models</code>
                <span className="text-ps-muted">Every model currently available on the platform</span>
              </div>
              <div className="flex flex-wrap justify-between gap-3 border-t border-ps-raised py-2.5 text-[13px]">
                <code className="text-ps-text">GET /v1/pricing</code>
                <span className="text-ps-muted">Final, all-inclusive INR price per model per 1M tokens</span>
              </div>
            </div>
            <p className={body}>
              Usage is metered per request and billed directly from your wallet balance - no separate invoicing step.{' '}
              <Link to="/console/models" className="ps-link">
                Browse models →
              </Link>
            </p>
          </section>

          {/* Errors */}
          <section id="errors" className="ps-card scroll-mt-24 overflow-x-auto" aria-labelledby="errors-heading">
            <div className="flex flex-col gap-2 px-[22px] pb-2 pt-[22px]">
              <h2 id="errors-heading" className={h2}>
                Errors
              </h2>
              <p className={body}>
                Error responses include a machine-readable <code className={ic}>code</code> field.
              </p>
            </div>
            <table className="ps-tbl min-w-[560px]">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Code</th>
                  <th>Meaning</th>
                  <th>What to do</th>
                </tr>
              </thead>
              <tbody>
                {ERROR_ROWS.map((row) => (
                  <tr key={row.code} className="align-top">
                    <td className="text-ps-text">{row.status}</td>
                    <td className="text-ps-text">{row.code}</td>
                    <td className="!whitespace-normal text-ps-muted">{row.meaning}</td>
                    <td className="!whitespace-normal text-ps-muted">{row.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Rate limits & quotas */}
          <section id="rate-limits" className={section}>
            <h2 className={h2}>Rate limits &amp; quotas</h2>
            <p className={body}>Each API key can be configured with its own limits:</p>
            <ul className="m-0 list-disc space-y-1 pl-5 text-[13px] text-ps-muted">
              <li>Requests per minute</li>
              <li>Tokens per minute</li>
              <li>An optional monthly token limit (unlimited by default)</li>
            </ul>
            <p className={body}>
              Configure these per-key on the{' '}
              <Link to="/console/keys" className="ps-link">
                API keys
              </Link>{' '}
              page. Requests that exceed a limit receive a <code className={ic}>429</code> response.
            </p>
          </section>
        </div>
      </div>
    </Page>
  );
};

export default Docs;
